import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ApiError,
  archiveAsset,
  deleteAsset,
  getAsset,
  publishAsset,
  updateAsset,
  uploadAssetVersion,
  type AssetDetail,
  type Audience,
  type DocumentType,
} from "@/api/client";
import { ASSET_STATUS_LABEL, AUDIENCE_LABEL, DOCUMENT_TYPE_SINGULAR } from "@/lib/asset-labels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { FileInput } from "@/components/ui/file-input";
import { FormError } from "@/components/ui/form-error";
import { InlineAlert } from "@/components/ui/inline-alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { usePopperAwareInteractOutside } from "@/lib/radix";
import { TaxonomyPicker } from "./TaxonomyPicker";

interface Props {
  assetId: string | null;
  token: string | undefined;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}

const AUDIENCES: Audience[] = ["my_learning", "for_clients"];
const DOC_TYPES: DocumentType[] = ["one_pager", "battlecard", "slide_deck", "case_study"];

/** Order-independent id-set equality, for comparing the applied topic/industry
 * selection against the saved asset. */
function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

/** Truthful, status-specific version-upload errors — mirrors
 * AddResourceDrawer's uploadErrorMessage so both upload paths speak the same
 * language (415/413/422/409 → a clear message, else a generic fallback). */
function versionUploadErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 415) return "Unsupported file type. Upload a PDF, PPTX, DOCX, or image.";
    if (err.status === 413) return "File too large. The maximum size is 25 MB.";
    if (err.status === 422) return "That file appears to be empty.";
    if (err.status === 409)
      return "A newer version already exists. Reload the panel and try again.";
  }
  return "Upload failed. Please check your connection and try again.";
}

/** Surface the server's error detail when present (e.g. a 409 conflict),
 * else a generic fallback, for the metadata/visibility mutations. */
function actionErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    if (typeof detail === "string" && detail.length > 0) return detail;
  }
  return fallback;
}

/** RM/RA manage panel (the mockup's profile slide-out): edit metadata, upload
 * a new version, publish/archive, delete, and review version history. */
export function AssetAdminPanel({ assetId, token, onOpenChange, onChanged }: Props) {
  const queryClient = useQueryClient();
  const onInteractOutside = usePopperAwareInteractOutside();
  const detail = useQuery<AssetDetail>({
    queryKey: ["assets", "detail", assetId, token],
    queryFn: () => getAsset(token!, assetId!),
    enabled: !!assetId && !!token,
  });

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState<Audience>("for_clients");
  const [documentType, setDocumentType] = useState<DocumentType>("one_pager");
  const [topicIds, setTopicIds] = useState<string[]>([]);
  const [industryIds, setIndustryIds] = useState<string[]>([]);
  // CHP-383: guard the destructive/visibility-changing actions behind a
  // confirmation step so a mis-click can't archive or delete an asset.
  const [confirming, setConfirming] = useState<"archive" | "delete" | null>(null);
  // CHP-804: inline failure feedback. `actionError` covers save/publish (shown
  // above the action buttons); the version upload and the two confirm dialogs
  // carry their own inline messages so each failure surfaces where it happened.
  const [actionError, setActionError] = useState<string | null>(null);
  const [versionError, setVersionError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  // CHP-809: the new-version control no longer uploads on file-select. Hold the
  // chosen file + optional change-notes so the upload carries the notes the
  // version-history list renders (they were previously always empty — there was
  // no input). `versionFileKey` resets the native FileInput after a success.
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionNotes, setVersionNotes] = useState("");
  const [versionFileKey, setVersionFileKey] = useState(0);

  // Seed the form from the asset ONCE per opened asset, not on every refetch.
  // A tag add/rename invalidates ["assets"] (so the cards update — CHP-392 #1),
  // which refetches this panel's detail query; without this guard that
  // background refetch would wipe a half-typed title and reset the dirty
  // baseline. detail.data still updates underneath to feed the isDirty compare.
  const initedFor = useRef<string | null>(null);
  useEffect(() => {
    const d = detail.data;
    if (assetId === null) {
      initedFor.current = null;
      return;
    }
    if (!d || initedFor.current === assetId) return;
    setTitle(d.title);
    setDescription(d.description ?? "");
    setAudience(d.audience);
    setDocumentType(d.document_type);
    setTopicIds(d.topics.map((t) => t.id));
    setIndustryIds(d.industries.map((i) => i.id));
    // Clear any version-control state carried over from a previously-managed
    // asset (the panel doesn't unmount between assets).
    setVersionFile(null);
    setVersionNotes("");
    setVersionError(null);
    initedFor.current = assetId;
  }, [detail.data, assetId]);

  const afterChange = () => {
    void queryClient.invalidateQueries({ queryKey: ["assets"] });
    onChanged();
  };

  const save = useMutation({
    mutationFn: () =>
      updateAsset(token!, assetId!, {
        title,
        description: description || null,
        audience,
        document_type: documentType,
        topic_ids: topicIds,
        industry_ids: industryIds,
      }),
    onSuccess: () => {
      setActionError(null);
      afterChange();
    },
    onError: (err) =>
      setActionError(actionErrorMessage(err, "Couldn’t save your changes. Please try again.")),
  });
  const publish = useMutation({
    mutationFn: () => publishAsset(token!, assetId!),
    onSuccess: () => {
      setActionError(null);
      afterChange();
    },
    onError: (err) =>
      setActionError(actionErrorMessage(err, "Couldn’t publish this asset. Please try again.")),
  });
  const archive = useMutation({
    mutationFn: () => archiveAsset(token!, assetId!),
    onSuccess: () => {
      setConfirmError(null);
      setConfirming(null);
      afterChange();
    },
    // Leave the ConfirmDialog open on failure and show the server detail so the
    // user can read why and retry from the dialog itself.
    onError: (err) =>
      setConfirmError(actionErrorMessage(err, "Couldn’t archive this asset. Please try again.")),
  });
  const remove = useMutation({
    mutationFn: () => deleteAsset(token!, assetId!),
    onSuccess: () => {
      setConfirmError(null);
      setConfirming(null);
      afterChange();
      onOpenChange(false);
    },
    // Same as archive: keep the dialog open and surface the server detail.
    onError: (err) =>
      setConfirmError(actionErrorMessage(err, "Couldn’t delete this asset. Please try again.")),
  });
  const newVersion = useMutation({
    mutationFn: (args: { file: File; changeNotes: string }) =>
      uploadAssetVersion(token!, assetId!, args.file, args.changeNotes.trim() || undefined),
    onSuccess: () => {
      setVersionError(null);
      // Clear the staged file + notes and force-remount the native input so the
      // control is ready for the next version.
      setVersionFile(null);
      setVersionNotes("");
      setVersionFileKey((k) => k + 1);
      afterChange();
    },
    onError: (err) => setVersionError(versionUploadErrorMessage(err)),
  });

  const d = detail.data;

  // "Save changes" only persists the asset's own fields + which tags are
  // *applied* to it. Disable it until one of those genuinely differs from the
  // saved asset, so the button itself signals that add/rename/delete of a tag
  // (which persist instantly via their own calls) need no save. CHP-392 #3.
  const isDirty =
    d != null &&
    (title !== d.title ||
      (description || "") !== (d.description ?? "") ||
      audience !== d.audience ||
      documentType !== d.document_type ||
      !sameIds(
        topicIds,
        d.topics.map((t) => t.id),
      ) ||
      !sameIds(
        industryIds,
        d.industries.map((i) => i.id),
      ));

  return (
    <Sheet open={assetId !== null} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-[420px] overflow-y-auto sm:max-w-[420px]"
        onInteractOutside={onInteractOutside}
      >
        <SheetHeader>
          <SheetTitle>Manage asset</SheetTitle>
          <SheetDescription>Edit metadata, version, publish or remove.</SheetDescription>
        </SheetHeader>

        {detail.isError ? (
          <div className="mt-6 space-y-3">
            <InlineAlert tone="error">Couldn’t load this asset. Please try again.</InlineAlert>
            <Button variant="outline" size="sm" onClick={() => void detail.refetch()}>
              Retry
            </Button>
          </div>
        ) : detail.isLoading || !d ? (
          <p role="status" className="mt-6 text-sm text-muted-foreground">
            Loading…
          </p>
        ) : (
          <div className="mt-6 space-y-5">
            <div className="flex items-center gap-2">
              <Badge variant={d.status === "published" ? "secondary" : "outline"}>
                {ASSET_STATUS_LABEL[d.status]}
              </Badge>
              {d.format ? <Badge variant="outline">{d.format}</Badge> : null}
              {d.version_number != null ? (
                <Badge variant="outline">v{d.version_number}</Badge>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manage-title">Title</Label>
              <Input id="manage-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manage-description">Description</Label>
              <Input
                id="manage-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Audience</Label>
                <Select value={audience} onValueChange={(v) => setAudience(v as Audience)}>
                  <SelectTrigger aria-label="Audience">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIENCES.map((a) => (
                      <SelectItem key={a} value={a}>
                        {AUDIENCE_LABEL[a]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Document type</Label>
                <Select
                  value={documentType}
                  onValueChange={(v) => setDocumentType(v as DocumentType)}
                >
                  <SelectTrigger aria-label="Document type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((dt) => (
                      <SelectItem key={dt} value={dt}>
                        {DOCUMENT_TYPE_SINGULAR[dt]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Topics</Label>
              <TaxonomyPicker
                kind="topic"
                token={token}
                selected={topicIds}
                onChange={setTopicIds}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Industries</Label>
              <TaxonomyPicker
                kind="industry"
                token={token}
                selected={industryIds}
                onChange={setIndustryIds}
              />
            </div>

            <Button
              onClick={() => save.mutate()}
              disabled={!isDirty}
              loading={save.isPending}
              loadingLabel="Saving…"
              className="w-full"
            >
              Save changes
            </Button>
            {save.isError || publish.isError ? <FormError>{actionError}</FormError> : null}

            <div className="space-y-2 border-t border-border pt-4">
              <Label htmlFor="manage-version">Upload new version</Label>
              <FileInput
                key={versionFileKey}
                id="manage-version"
                disabled={newVersion.isPending}
                onChange={(e) => setVersionFile(e.target.files?.[0] ?? null)}
              />
              <div className="space-y-1.5">
                <Label htmlFor="manage-version-notes">What changed? (optional)</Label>
                <Input
                  id="manage-version-notes"
                  value={versionNotes}
                  placeholder="e.g. Updated Q3 pricing"
                  disabled={newVersion.isPending}
                  onChange={(e) => setVersionNotes(e.target.value)}
                />
              </div>
              <Button
                size="sm"
                disabled={!versionFile}
                loading={newVersion.isPending}
                loadingLabel="Uploading…"
                onClick={() => {
                  if (versionFile)
                    newVersion.mutate({ file: versionFile, changeNotes: versionNotes });
                }}
              >
                Upload version
              </Button>
              {newVersion.isError ? <FormError>{versionError}</FormError> : null}
            </div>

            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              {d.status !== "published" ? (
                <Button variant="outline" size="sm" onClick={() => publish.mutate()}>
                  Publish
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    archive.reset();
                    setConfirmError(null);
                    setConfirming("archive");
                  }}
                >
                  Archive
                </Button>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  remove.reset();
                  setConfirmError(null);
                  setConfirming("delete");
                }}
              >
                Delete
              </Button>
            </div>

            <ConfirmDialog
              open={confirming === "archive"}
              onOpenChange={(open) => {
                if (!open) {
                  setConfirming(null);
                  setConfirmError(null);
                }
              }}
              title="Archive this asset?"
              description={`"${d.title}" will be unpublished and hidden from standard users. You can republish it from this panel.`}
              confirmLabel="Archive"
              variant="destructive"
              pending={archive.isPending}
              onConfirm={() => archive.mutate()}
            >
              {archive.isError ? <FormError>{confirmError}</FormError> : null}
            </ConfirmDialog>
            <ConfirmDialog
              open={confirming === "delete"}
              onOpenChange={(open) => {
                if (!open) {
                  setConfirming(null);
                  setConfirmError(null);
                }
              }}
              title="Delete this asset?"
              description={`"${d.title}" will be permanently removed for everyone. This can't be undone.`}
              confirmLabel="Delete"
              variant="destructive"
              pending={remove.isPending}
              onConfirm={() => remove.mutate()}
            >
              {remove.isError ? <FormError>{confirmError}</FormError> : null}
            </ConfirmDialog>

            <div className="border-t border-border pt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Version history
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {d.versions
                  .slice()
                  .reverse()
                  .map((v) => (
                    <li key={v.id}>
                      v{v.version_number} · {(v.size_bytes / 1024).toFixed(0)} KB
                      {v.change_notes ? ` · ${v.change_notes}` : ""}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
